import { Controller } from "@hotwired/stimulus";
import { onAnimationEnd } from "@theme/utilities";

class HeaderDrawerController extends Controller {
  static targets = [
    "drawer",
    "panel",
    "panelTitle",
    "backButton",
    "logo",
    "titleText",
  ];
  connect() {
    this._onDrawerClick = this.#handleDrawerClick.bind(this);
    if (this.hasDrawerTarget) {
      this.drawerTarget.addEventListener("click", this._onDrawerClick);
    }
  }

  disconnect() {
    if (this.hasDrawerTarget) {
      this.drawerTarget.removeEventListener("click", this._onDrawerClick);
    }
  }

  toggleVisibility() {
    const details = this.element;
    const summary = details.querySelector("summary");

    if (!summary) {
      return;
    }

    const isOpen = details.hasAttribute("open");

    //this.preventInitialAccordionAnimations(details);

    this.resetPanels();

    // CLOSE
    if (isOpen) {
      details.classList.remove("menu-open");
      summary.setAttribute("aria-expanded", "false");
      // Wait for the .menu-drawer element's transition, not the entire details subtree
      // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
      const drawer = details.querySelector(
        ".menu-drawer, .menu-drawer__submenu",
      );

      onAnimationEnd(
        drawer || details,
        () => {
          details.removeAttribute("open");
        },
        { subtree: false },
      );
    } else {
      // OPEN
      requestAnimationFrame(() => {
        details.classList.add("menu-open");
        summary.setAttribute("aria-expanded", "true");
        details.setAttribute("open", "");
      });
    }
  }

  preventClickPropagation(event) {
    event.stopPropagation();
  }

  openPanel(event) {
    const level = parseInt(event.currentTarget.dataset.level);
    const title = event.currentTarget.dataset.title;
    const parentTitle =
      event.currentTarget.closest("[data-headerdrawer-target='panel']")?.dataset
        .title || "root";

    this.currentLevel = level;
    this.currentTitle = title;
    this.currentParentTitle = parentTitle;

    this.panelTargets.forEach((panel) => {
      const panelLevel = parseInt(panel.dataset.level);
      const panelTitle = panel.dataset.title;
      const panelParent = panel.dataset.parent || "root";

      const isCurrent =
        panelLevel === level &&
        panelTitle === title &&
        (panelLevel === 1 || panelParent === parentTitle);

      if (panelLevel === level) {
        panel.style.transform = isCurrent
          ? "translateX(0)"
          : "translateX(100%)";
      } else if (panelLevel < level) {
        panel.style.transform = "translateX(-100%)";
      } else {
        panel.style.transform = "translateX(100%)";
      }
    });

    if (this.hasLogoTarget) this.logoTarget.classList.add("tw:hidden");
    if (this.hasTitleTextTarget) {
      this.titleTextTarget.classList.remove("tw:hidden");
      this.titleTextTarget.textContent = title;
    }
    if (this.hasBackButtonTarget)
      this.backButtonTarget.classList.remove("tw:hidden");

    if (window.posthog) {
      window.posthog.capture("menu_dropdown_open", {
        panel_title: title,
        panel_level: level,
        parent_menu: parentTitle,
        device_type: "mobile",
      });
    }
  }

  goBack() {
    if (this.currentLevel === 0) return;

    const prevLevel = this.currentLevel - 1;
    const targetTitle =
      this.currentLevel === 2 ? this.currentParentTitle : null;

    this.panelTargets.forEach((panel) => {
      const level = parseInt(panel.dataset.level);
      const panelTitle = panel.dataset.title;

      if (level === prevLevel) {
        const shouldShow =
          this.currentLevel === 2 ? panelTitle === targetTitle : level === 0;

        panel.style.transform = shouldShow
          ? "translateX(0)"
          : "translateX(100%)";

        if (shouldShow && this.hasTitleTextTarget) {
          this.titleTextTarget.textContent = panelTitle || "";
        }

        if (shouldShow && level === 0 && this.hasLogoTarget) {
          this.logoTarget.classList.remove("tw:hidden");
          this.titleTextTarget.classList.add("tw:hidden");
          this.backButtonTarget.classList.add("tw:hidden");
        }
      }

      if (level >= this.currentLevel) {
        panel.style.transform = "translateX(100%)";
      }
    });

    this.currentLevel = prevLevel;
  }

  resetPanels() {
    this.panelTargets.forEach((panel) => {
      const level = parseInt(panel.dataset.level);
      panel.style.transform =
        level === 0 ? "translateX(0)" : "translateX(100%)";
    });

    if (this.hasLogoTarget) this.logoTarget.classList.remove("tw:hidden");
    if (this.hasTitleTextTarget)
      this.titleTextTarget.classList.add("tw:hidden");
    if (this.hasBackButtonTarget)
      this.backButtonTarget.classList.add("tw:hidden");

    this.currentLevel = 0;
    this.currentTitle = null;
    this.currentParentTitle = null;
  }
  #handleDrawerClick(event) {
    const link = event.target.closest("a[href]");
    if (!link) return;

    // escludi link utility (account/ordini) marcati con data-skip-track
    if (link.closest("[data-skip-track]")) return;

    const panel = link.closest("[data-headerdrawer-target='panel']");
    if (!panel) return;

    if (window.posthog) {
      window.posthog.capture("menu_dropdown_link_click", {
        link_label: link.textContent.trim(),
        link_url: link.getAttribute("href"),
        parent_menu: panel.dataset.title || null,
        panel_level: panel.dataset.level ? parseInt(panel.dataset.level, 10) : null,
        device_type: "mobile",
      });
    }
  }
}

Stimulus.register("headerDrawer", HeaderDrawerController);
